import { Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { useParams } from "react-router-dom";
import { usePortfolioQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioQuery";
import { usePortfolioRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import { ClientPortfolioDomainEnum } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import {
	PortfolioDetailKind,
	PortfolioDetailMenu,
} from "web_myplann/Portfolio/Components/PortfolioDetailMenu";
import { BankProduct } from "web_myplann/Portfolio/Components/PortfolioSections/BankProduct";
import { BuildingSavings } from "web_myplann/Portfolio/Components/PortfolioSections/BuildingSavings";
import { Commodities } from "web_myplann/Portfolio/Components/PortfolioSections/Commodities";
import { Cryptocurrency } from "web_myplann/Portfolio/Components/PortfolioSections/Cryptocurrency";
import { Investment } from "web_myplann/Portfolio/Components/PortfolioSections/Investment";
import { LifeInsurance } from "web_myplann/Portfolio/Components/PortfolioSections/LifeInsurance";
import { LoanAll } from "web_myplann/Portfolio/Components/PortfolioSections/LoanAll";
import { PaidAdvisory } from "web_myplann/Portfolio/Components/PortfolioSections/PaidAdvisory";
import { PensionProduct } from "web_myplann/Portfolio/Components/PortfolioSections/PensionProduct";
import { PropertyInsurance } from "web_myplann/Portfolio/Components/PortfolioSections/PropertyInsurance";
import { Services } from "web_myplann/Portfolio/Components/PortfolioSections/Services";
import { hasNoBusinessData } from "web_myplann/Portfolio/Helpers/hasNoBusinessData";
import { usePortfolioTitle } from "web_myplann/Portfolio/Hooks/usePortfolioTitle";
import { StyledCenteredStack } from "web_myplann/Shared/Components/StyledCenteredStack";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { RowSkeletonLoader } from "web_myplann/Shared/RowSkeletonLoader";
import { getTypedParam } from "web_myplann/Shared/Utils/getTypedParam";

export const PortfolioDetailPage: FC = () => {
	const routeParams = useParams<{ id: string }>();
	const portfolioId = getTypedParam(routeParams, "id", Number);
	const { t } = useResource();
	const theme = useTheme();

	const { data: portfolio, isLoading } = usePortfolioQuery(portfolioId);
	const hasPortfolioPilotRight = usePortfolioRightOptional();

	const title = usePortfolioTitle(portfolio, t);

	const currencySign =
		portfolio?.currencySign ?? t(Resources.Common.CurrencySign);

	const showEmpty = !isLoading && hasNoBusinessData(portfolio);

	const domain = portfolio?.domain ?? undefined;
	const showDetailMenu =
		hasPortfolioPilotRight && domain !== undefined && portfolioId !== undefined;

	return (
		<MyPlannLayout
			topBar={
				<NavigationHeader
					title={title}
					defaultRoute={Routing.Portfolio.PortfolioList.path()}
					alwaysUseDefaultRoute
					endComponent={
						showDetailMenu ? (
							<PortfolioDetailMenu
								kind={PortfolioDetailKind.Portfolio}
								id={portfolioId}
								domain={domain}
								title={title}
							/>
						) : undefined
					}
				/>
			}
			disableBottomPadding
		>
			{isLoading ? (
				<DetailSectionLoader />
			) : showEmpty ? (
				<StyledCenteredStack spacing={2} px={5}>
					<RowSkeletonLoader />
					<RowSkeletonLoader />
					<RowSkeletonLoader />
					<Stack textAlign="center" gap={0.4}>
						<Typography
							variant="deprecatedH3"
							color={theme.palette.deprecated.grey[700]}
						>
							{t(Resources.PortfolioDetail.SharedLabels.empty)}
						</Typography>
						<Typography
							variant="deprecatedBody1"
							color={theme.palette.deprecated.grey[500]}
						>
							{t(Resources.PortfolioDetail.SharedLabels.emptySubtitle)}
						</Typography>
					</Stack>
				</StyledCenteredStack>
			) : (
				<Stack pt={2}>
					{portfolio?.domain === ClientPortfolioDomainEnum.LifeInsurance && (
						<LifeInsurance baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.Investments && (
						<Investment baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain ===
						ClientPortfolioDomainEnum.PropertyInsurance && (
						<PropertyInsurance
							baseData={portfolio}
							currencySign={currencySign}
						/>
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.PensionProducts && (
						<PensionProduct baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.BuildingSavings && (
						<BuildingSavings baseData={portfolio} currencySign={currencySign} />
					)}
					{(portfolio?.domain === ClientPortfolioDomainEnum.Loans ||
						portfolio?.domain === ClientPortfolioDomainEnum.Leasing ||
						portfolio?.domain === ClientPortfolioDomainEnum.BusinessLoans) && (
						<LoanAll baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.Commodities && (
						<Commodities baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.BankingProducts && (
						<BankProduct baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.Cryptocurrencies && (
						<Cryptocurrency baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.Services && (
						<Services baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.PaidAdvisory && (
						<PaidAdvisory baseData={portfolio} currencySign={currencySign} />
					)}
				</Stack>
			)}
		</MyPlannLayout>
	);
};
